import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/projects", label: "Projects" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "Studio" },
    { to: "/studio-life", label: "Studio Life" },
    { to: "/contact", label: "Contact" },
];

// Routes that render a full-bleed dark hero — nav must read as white at the top.
const HERO_ROUTES = [/^\/$/, /^\/projects\/[^/]+$/];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    const overHero = !scrolled && HERO_ROUTES.some((r) => r.test(location.pathname));

    // Tokenised text colors based on context
    const inkPrimary = overHero ? "#F9F8F6" : "#1A1A1A";
    const inkMuted = overHero ? "rgba(249,248,246,0.75)" : "#5C5855";
    const inkAccent = overHero ? "rgba(249,248,246,0.65)" : "#8C7A6B";

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#E5E1DB]"
                    : overHero
                    ? "bg-gradient-to-b from-[#1A1A1A]/45 to-transparent border-b border-transparent"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="px-6 md:px-12 lg:px-24">
                <div className="flex items-center justify-between h-20 md:h-24">
                    <Link
                        to="/"
                        data-testid="nav-home-link"
                        className="flex items-center leading-none"
                        aria-label="ASC Interior — Home"
                    >
                        <img
                            src={overHero ? "/asc-logo-hero.svg" : "/asc-logo-dark.svg"}
                            alt="ASC Interior"
                            className="h-10 md:h-12 lg:h-14 w-auto select-none transition-opacity duration-300"
                        />
                    </Link>

                    <nav className="hidden md:flex items-center gap-9">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="asc-link text-sm tracking-wide uppercase font-medium transition-colors duration-300"
                                style={({ isActive }) => ({
                                    color: isActive ? inkPrimary : inkMuted,
                                    letterSpacing: "0.18em",
                                    fontSize: "11px",
                                })}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className={`hidden md:inline-flex items-center px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                            overHero
                                ? "bg-[#F9F8F6] text-[#1A1A1A] hover:bg-[#E5E1DB]"
                                : "bg-[#1A1A1A] text-[#F9F8F6] hover:bg-[#3E3A37]"
                        }`}
                    >
                        Start a project
                    </Link>

                    <button
                        type="button"
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((o) => !o)}
                        className="md:hidden p-2 -mr-2 transition-colors duration-300"
                        style={{ color: inkPrimary }}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden bg-[#F9F8F6] border-t border-[#E5E1DB] px-6 py-8"
                >
                    <div className="flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className="serif text-2xl text-[#1A1A1A]"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta"
                            className="mt-4 inline-flex items-center justify-center px-6 py-4 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em]"
                        >
                            Start a project
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
